import { PostPreview } from '../Previews/PostPreview'
import Styles from './PostPreviewBody.module.scss'
import { PreviewInput } from './PreviewInput'
import { ExternalIntegrationSelect } from 'components/ExternalIntegrationSelect/ExternalIntegrationSelect'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Text } from 'blaze-ui/Text/Text'
import {
  ArticlePostPreviewActionTypes,
  MediaFileMetadata,
  maxCharacters,
  maxFileSize,
  maxVideoSize,
  maxFiles,
  providerFromChannelType,
  useArticlePostPreviewContext,
} from '../ArticlePostPreviewContext'
import { TextPlatformImages } from 'components/ArticlePostPreview/Previews/components/TextPlatformImages'
import { Button } from 'blaze-ui/Button'
import AlertTriangle from 'blaze-ui/icons/20/AlertTriangle'
import { useModals } from 'almanac-ui/Modal'
import { MediaUploadModal } from 'blaze-components/MediaUploadModal/MediaUploadModal'
import cx from 'classnames'
import { usePostProperties } from '../usePostProperties'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import {
  SocialChannelType,
  platformKeyByChannel,
  platformNameFromChannel,
} from 'common/ShareSheetV2/Screens/Default/Default'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { MobilePostPreviewBody } from './MobilePostPreviewBody'
import { AssigneeTypeFromAssetType } from 'hooks/useAlmanacFileSubscription'
import { Error } from './Error'
import { useHover } from '@react-aria/interactions'
import { Edit1 } from 'blaze-ui/icons'
import { LoadingSkeleton } from './LoadingSkeleton/LoadingSkeleton'
import { useAuthedArticle, useCreateOrUpdateScheduledPost } from '../hooks'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Provider } from 'components/ImagePickerModal/Types'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { IntegrationRequiredModal } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { Can } from 'components/Can'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AlmanacFileSummary, MediaObject, SelectedImage, UserRole } from 'types'
import { ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { useMediaFileUploadProgress } from '../useMediaFileUploadProgress'
import { ReactNode, useEffect, useRef } from 'react'
import { PollScheduledPostStatus } from './PollScheduledPostStatus/PollScheduledPostStatus'
import { ImagePickerContextState } from 'components/ImagePickerModal/ImagePickerContextTypes'
import { useCreateMediaObjectsWithoutPresigning } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { ValidationBanner } from 'components/ValidationBanner/ValidationBanner'
import { Tooltip } from 'components/Tooltip/Tooltip'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { useErrorAction } from './hooks'
import { ScheduledPostErrorType } from 'api/clients/ScheduledPostsClient'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { usePostValidation } from './usePostValidation'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'

const CanEditPost = ({
  almanacFile,
  children,
}: {
  almanacFile: Omit<AlmanacFileSummary, 'ancestors'>
  children: ReactNode
}) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const workspace = useCurrentWorkspace()
  const articleMeta = useCurrentArticleMeta()
  const authedArticle = useAuthedArticle(almanacFile)

  if (articleMeta?.main_layer.public && workspace?.role === UserRole.UNAUTHED) return <></>

  return (
    <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId}>
      {children}
    </Can>
  )
}

export const PostPreviewBody = () => {
  const {
    state: { content, loading, channel, files, editing, errorMessage, errorType, uploadingMediaFilesMetadata },
    validationErrors,
    dispatch,
    almanacFile,
    scheduledPost,
    updateContent,
  } = useArticlePostPreviewContext()
  const integration = scheduledPost?.external_integration
  const { openModal, closeModal } = useModals()
  const { isMobile } = useResponsiveDesign()
  const assigneeType = AssigneeTypeFromAssetType[almanacFile.asset_type]
  const { status } = usePostProperties({ id: almanacFile.id, type: assigneeType })
  let { hoverProps, isHovered } = useHover({})
  const { data } = useExternalIntegrations()
  const integrations = data?.external_integrations
  const { onUploadStart, onUploadEnd, onUploadProgress } = useMediaFileUploadProgress()
  const metadataRef = useRef<MediaFileMetadata[]>([])
  const openWindowSafely = useOpenWindowSafely()
  const { errorAction } = useErrorAction({ integration, errorType })
  const { mutateAsync: createMediaObjectsWithoutPresigning } = useCreateMediaObjectsWithoutPresigning()
  const isNewImageMenuEnabled = useFlipperFlag('new_image_menu')
  const handleSubmit = async (selectedUploadFiles: SelectedImage[], context: ImagePickerContextState) => {
    const maxFilesCount = integration ? maxFiles[integration.provider] : Infinity
    let newFiles

    if (['uploads', 'ai', 'brandKits'].includes(context.provider || '')) {
      newFiles = await createMediaObjectsWithoutPresigning(
        selectedUploadFiles.map(({ id, ...file }, index) => ({
          ...file,
          ...metadataRef.current[index],
          url: file.mime_type?.startsWith('video') && file.source_url ? file.source_url : file.url,
        })),
      )
    } else {
      newFiles = selectedUploadFiles.map((file) => ({
        id: parseInt(file.id as string),
        source_url: file.url,
        mime_type: file.mime_type as string,
      })) as MediaObject[]
    }

    let metadatas = [...metadataRef.current]
    const newFilesUpdated = newFiles.map((file) => {
      const metadata = metadatas.shift()

      return {
        ...file,
        ...metadata,
      }
    })

    dispatch({
      type: ArticlePostPreviewActionTypes.SET_FILES,
      payload: [...files, ...newFilesUpdated].slice(0, maxFilesCount),
    })
    onUploadEnd()
  }

  // Need to keep the reference to the metadata array from the article post preview context and pass that
  // to the MediaUploadModal's handleSubmit callback, otherwise the value will be stale
  useEffect(() => {
    metadataRef.current = uploadingMediaFilesMetadata
  }, [uploadingMediaFilesMetadata])

  const openFileUploadModal = () => {
    const maxCount = integration ? maxFiles[integration.provider] - files.length : Infinity

    if (isNewImageMenuEnabled) {
      openModal(ImageMenuModal, {
        onUploadProgress,
        onUploadStart,
        onUploadEnd,
        maxCount,
        hiddenProviders: ['gifs'],
        uploadMedia: true,
        onUse: async (images: ImageMenuSelectedImage[]) => {
          const maxFilesCount = integration ? maxFiles[integration.provider] : Infinity
          let newFiles: MediaObject[] = []

          // Process images sequentially with Promise.all to wait for all async operations
          await Promise.all(
            images.map(async (image, index) => {
              const { id, ...file } = image
              const mediaObjects = await createMediaObjectsWithoutPresigning([
                {
                  ...file,
                  ...metadataRef.current[index],
                  url: file.mime_type?.startsWith('video') && file.source_url ? file.source_url : file.url,
                },
              ])
              newFiles.push(mediaObjects[0])
            }),
          )

          let metadatas = [...metadataRef.current]
          const newFilesUpdated = newFiles.map((file) => {
            const metadata = metadatas.shift()

            return {
              ...file,
              ...metadata,
            }
          })

          dispatch({
            type: ArticlePostPreviewActionTypes.SET_FILES,
            payload: [...files, ...newFilesUpdated].slice(0, maxFilesCount),
          })

          closeModal()
        },
      })

      return
    }

    openModal(MediaUploadModal, {
      maxCount,
      hiddenProviders: new Set<Provider>(['gifs']),
      title: 'Add Images or Videos',
      accept: ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES,
      onUploadProgress,
      onUploadStart,
      handleSubmit,
    })
  }

  const isReadOnly = ({
    editing,
    almanacFile,
  }: {
    editing: boolean
    almanacFile: Omit<AlmanacFileSummary, 'ancestors'>
  }): boolean => {
    return (
      !editing ||
      !almanacFile.permissions.can_edit ||
      (almanacFile.permissions.access !== 'edit' && almanacFile.permissions.access !== 'owner')
    )
  }

  const { createOrUpdateScheduledPost } = useCreateOrUpdateScheduledPost({
    almanacFile: almanacFile,
    provider: providerFromChannelType[channel as SocialChannelType],
  })

  const updateIntegrationIdImmediately = async (externalIntegrationId: number) => {
    try {
      await createOrUpdateScheduledPost({
        externalIntegrationId,
      })
      showToastMessage('Account changed!', 'success')
    } catch {
      showToastMessage('Failed to change account', 'error')
    }
  }

  const hasIntegrationForChannel = integrations?.some(
    (integration) => integration.provider === platformKeyByChannel[channel as SocialChannelType],
  )

  const provider = integration?.provider || providerFromChannelType[channel as ChannelType]

  const isMediaError = errorType === ScheduledPostErrorType.MediaFormatError
  const postingFailed = status?.value === PostStatusPropertyValue.Failed
  const errors = errorMessage && postingFailed && isMediaError ? [errorMessage, ...validationErrors] : validationErrors

  const { characterLimitErrors, otherErrors, selectedPlatform } = usePostValidation({
    errors,
    provider,
  })

  if (isMobile) return <MobilePostPreviewBody />

  if (loading) return <LoadingSkeleton />

  return (
    <div className={Styles.previewBody}>
      {scheduledPost && <PollScheduledPostStatus scheduledPost={scheduledPost} />}
      {errorMessage && postingFailed && !isMediaError && (
        <Error errorMessages={[errorMessage]} action={errorAction()} />
      )}
      {characterLimitErrors.length > 0 && selectedPlatform && (
        <ValidationBanner content={content} selectedPlatform={selectedPlatform} />
      )}
      {otherErrors.length > 0 && (
        <Error
          errorMessages={otherErrors}
          action={{
            label: 'See Requirements',
            onPress: () =>
              openWindowSafely(
                'https://help.blaze.ai/en/articles/9911718-image-and-video-file-requirements-by-platform-connection',
              ),
          }}
        />
      )}
      <div className={Styles.previewBodyContent} {...hoverProps}>
        <CanEditPost almanacFile={almanacFile}>
          <div className={Styles.previewBodyLeft}>
            <div className={cx(Styles.previewSection, 'social-media-doc-tour-1', !editing && Styles.readOnly)}>
              <PreviewInput
                readOnly={isReadOnly({ editing, almanacFile })}
                isHovered={isHovered}
                value={content}
                onChange={(e, shouldPersistChange) => {
                  dispatch({ type: ArticlePostPreviewActionTypes.SET_CONTENT, payload: e.target.value })
                  if (shouldPersistChange) updateContent(e.target.value)
                }}
                maxCharacterCount={provider ? maxCharacters[provider] : undefined}
              />
            </div>
          </div>
        </CanEditPost>
        <div className={Styles.previewSection}>
          <PostPreview
            openFileUploadPrompt={openFileUploadModal}
            content={content}
            channel={channel}
            integration={integration}
            mediaObjects={files}
          />
        </div>
      </div>
      <div className={Styles.previewBodyFooter}>
        <CanEditPost almanacFile={almanacFile}>
          <TextPlatformImages
            files={files}
            provider={provider}
            readOnly={!editing}
            setFiles={(files) => {
              dispatch({ type: ArticlePostPreviewActionTypes.SET_FILES, payload: files })
            }}
            integration={integration}
            openFileUploadModal={openFileUploadModal}
            maxSize={provider ? maxFileSize[provider] : undefined}
            maxVideoSize={provider ? maxVideoSize[provider] : undefined}
          />
        </CanEditPost>
        <div className={Styles.previewBodyFooterControls}>
          <div>
            {!editing && (
              <CanEditPost almanacFile={almanacFile}>
                <Tooltip
                  label="Move post back to Draft to edit it"
                  placement="top"
                  hide={status.value !== PostStatusPropertyValue.Posted}
                >
                  <Button
                    frontIcon={Edit1}
                    key="edit"
                    variant="secondary"
                    size="sm"
                    isDisabled={scheduledPost?.is_posting}
                    onPress={() => {
                      if (status.value === PostStatusPropertyValue.Posted) return

                      dispatch({ type: ArticlePostPreviewActionTypes.SET_EDITING, payload: true })
                    }}
                  >
                    Edit Post Content
                  </Button>
                </Tooltip>
              </CanEditPost>
            )}
          </div>
          <CanEditPost almanacFile={almanacFile}>
            <div className={Styles.previewBodyFooterRight}>
              <Text variant="secondary">{platformNameFromChannel[channel as SocialChannelType]} Account:</Text>
              {hasIntegrationForChannel ? (
                <ExternalIntegrationSelect
                  externalIntegrations={integrations ?? []}
                  size="sm"
                  selectedExternalIntegrationId={integration?.id}
                  onChange={updateIntegrationIdImmediately}
                  primaryProvider={platformKeyByChannel[channel as SocialChannelType] as ExternalIntegrationEnum}
                  forceProvider={platformKeyByChannel[channel as SocialChannelType] as ExternalIntegrationEnum}
                  isDisabled={scheduledPost?.is_posting}
                />
              ) : (
                <Button
                  variant="danger"
                  frontIcon={AlertTriangle}
                  onPress={() =>
                    openModal(IntegrationRequiredModal, { integration: provider as ExternalIntegrationEnum })
                  }
                  size="sm"
                >
                  Not Connected
                </Button>
              )}
            </div>
          </CanEditPost>
        </div>
      </div>
    </div>
  )
}
